import React from "react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LightModeIcon from "@mui/icons-material/LightMode";
import NightsStayIcon from "@mui/icons-material/NightsStay";
// Theme toggle icons stay as MUI (no custom equivalent needed)
import {
  alpha,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { RushaMark } from "@shell/components/RushaMark";
import { useThemeManager } from "src/platform/hooks/useThemeManager";

import NotificationsMenu from "./NotificationsMenu";
import UserMenu from "./UserMenu";

const TOPBAR_HEIGHT = 56;

interface TopbarProps {
  breadcrumbs?: React.ReactNode;
  onExpandSidebar?: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ breadcrumbs, onExpandSidebar }) => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeManager();

  const iconBtnSx = {
    width: 34,
    height: 34,
    borderRadius: "8px",
    color: "text.secondary",
    "&:hover": {
      color: "text.primary",
      bgcolor: alpha(theme.palette.primary.main, 0.06),
    },
  };

  return (
    <Box
      component="header"
      sx={{
        height: TOPBAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        position: "sticky",
        top: 0,
        zIndex: 110,
        backgroundColor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
      }}
    >
      {/* Left: Brand */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginRight: "24px",
          flexShrink: 0,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={onExpandSidebar}
      >
        <RushaMark size={28} />
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: "15px",
            color: "text.primary",
            letterSpacing: "-0.01em",
          }}
        >
          Rusha
        </Typography>
      </Box>

      {/* Center: Breadcrumbs */}
      <Box
        sx={{
          flex: 1,
          fontSize: "13px",
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {breadcrumbs}
      </Box>

      {/* Right: Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
        }}
      >
        <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
          <IconButton
            onClick={toggleColorMode}
            aria-label="Switch theme"
            sx={iconBtnSx}
          >
            {mode === "dark" ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <NightsStayIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Help">
          <IconButton aria-label="Help" sx={iconBtnSx}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <NotificationsMenu />

        <Box sx={{ marginLeft: "4px" }}>
          <UserMenu />
        </Box>
      </Box>
    </Box>
  );
};

export default Topbar;
